import React, { useState, useEffect } from 'react';
import { ArrowLeft, Timer, HelpCircle, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import errorSound from '../assets/sounds/error2.mp3';
import successSound from '../assets/sounds/success.wav';

interface PrimeHunterGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

const PrimeHunterGame: React.FC<PrimeHunterGameProps> = ({ mode, onReset, language }) => {
  const [numbers, setNumbers] = useState<number[]>([]);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [remainingTime, setRemainingTime] = useState(300); // 5 minutes
  const [showHints, setShowHints] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [level, setLevel] = useState(1);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [showGameComplete, setShowGameComplete] = useState(false);
  const [remainingPrimes, setRemainingPrimes] = useState(0);
  const maxNumber = level === 1 ? 50 : level === 2 ? 75 : 100;

  const [playError] = useSound(errorSound, { volume: 0.3 });
  const [playSuccess] = useSound(successSound, { volume: 0.5 });

  useEffect(() => {
    initializeGame();
  }, [level]);

  useEffect(() => {
    if (mode === 'timed' && !gameOver) {
      const timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setGameOver(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [mode, gameOver]);

  const isPrime = (num: number): boolean => {
    if (num <= 1) return false;
    if (num <= 3) return true;
    if (num % 2 === 0 || num % 3 === 0) return false;
    
    for (let i = 5; i * i <= num; i += 6) {
      if (num % i === 0 || num % (i + 2) === 0) return false;
    }
    return true;
  };

  const initializeGame = () => {
    const nums = Array.from({ length: maxNumber }, (_, i) => i + 1);
    setNumbers(nums);
    setSelectedNumbers([]);
    setGameOver(false);
    setScore(0);
    setMistakes(0);
    setRemainingTime(300);
    // Calculate initial remaining primes
    const primeCount = nums.filter(isPrime).length;
    setRemainingPrimes(primeCount);
  };

  const handleNumberClick = async (number: number) => {
    if (gameOver || selectedNumbers.includes(number)) return;

    const isNumberPrime = isPrime(number);
    
    if (isNumberPrime) {
      setScore((prev) => prev + 1);
      setSelectedNumbers((prev) => [...prev, number]);
      setRemainingPrimes((prev) => prev - 1);
      playSuccess();
    } else {
      playError();
      setMistakes((prev) => prev + 1);
      if (mode === 'expert') {
        setGameOver(true);
        saveProgress();
      }
    }

    // Check if all prime numbers have been found
    const remainingPrimes = numbers.filter(n => !selectedNumbers.includes(n) && isPrime(n));
    if (remainingPrimes.length === 1 && isNumberPrime) {
      if (level < 3) {
        setShowLevelComplete(true);
        setTimeout(() => {
          setShowLevelComplete(false);
          setLevel(prev => prev + 1);
        }, 2000);
      } else {
        setShowGameComplete(true);
        saveProgress();
      }
    }
  };

  const saveProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const totalPrimes = numbers.filter(isPrime).length;
      const accuracy = Math.round((score / (score + mistakes)) * 100);

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'prime',
        mode: 'detective',
        questions_answered: score + mistakes,
        correct_answers: score,
        average_response_time: mode === 'timed' ? (300 - remainingTime) / score : 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setLevel(1);
    setScore(0);
    setGameOver(false);
    setRemainingTime(300);
    setMistakes(0);
    setShowLevelComplete(false);
    setShowGameComplete(false);
    setSelectedNumbers([]);
    initializeGame();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          {mode === 'timed' && (
            <div className={`flex items-center gap-2 ${
              remainingTime <= 10 
                ? 'text-red-600 dark:text-red-400' 
                : 'text-indigo-600 dark:text-indigo-400'
            }`}>
              <Timer className="h-5 w-5" />
              <span className="font-bold">{formatTime(remainingTime)}</span>
            </div>
          )}
          {mode !== 'expert' && (
            <button
              onClick={() => setShowHints(!showHints)}
              className={`p-2 rounded-lg transition-colors ${
                showHints 
                  ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300' 
                  : 'text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300'
              }`}
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          )}
          <div className="text-lg font-medium text-indigo-700 dark:text-indigo-300">
            {language === 'en' ? `Level ${level}` : `Niveau ${level}`}
          </div>
        </div>
      </div>

      {(showLevelComplete || showGameComplete) && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center shadow-xl
                          transform scale-95 opacity-0 animate-[fadeZoomIn_0.5s_ease-out_forwards]">
            <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {showGameComplete 
                ? (language === 'en' ? 'Congratulations!' : 'Félicitations !')
                : (language === 'en' ? 'Level Complete!' : 'Niveau Terminé !')}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
              {showGameComplete
                ? (language === 'en' 
                    ? 'You\'ve completed all levels!'
                    : 'Vous avez terminé tous les niveaux !')
                : (language === 'en' 
                    ? `Moving to Level ${level + 1}`
                    : `Passage au Niveau ${level + 1}`)}
            </p>
            {showGameComplete && (
              <button
                onClick={resetGame}
                className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
                  hover:bg-indigo-700 dark:hover:bg-indigo-600 
                  focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2"
              >
                {language === 'en' ? 'Play Again' : 'Rejouer'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
        <div className="mb-6 flex justify-between items-center">
          <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {language === 'en' ? `Level ${level}` : `Niveau ${level}`}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-emerald-600 dark:text-emerald-400">
              {language === 'en' ? 'Score: ' : 'Score : '}{score}
            </div>
            {mode !== 'expert' && (
              <div className="text-red-600 dark:text-red-400">
                {language === 'en' ? 'Mistakes: ' : 'Erreurs : '}{mistakes}
              </div>
            )}
            <div className="text-indigo-600 dark:text-indigo-400">
              {language === 'en' 
                ? `Remaining primes: ${remainingPrimes}`
                : `Nombres premiers restants : ${remainingPrimes}`}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
          {numbers.map((number) => {
            const isSelected = selectedNumbers.includes(number);
            const isPrimeNumber = isPrime(number);
            const showHint = showHints && isPrimeNumber && !isSelected;

            return (
              <button
                key={number}
                onClick={() => handleNumberClick(number)}
                disabled={isSelected || gameOver}
                className={`
                  aspect-square rounded-lg font-medium text-lg
                  flex items-center justify-center transition-all
                  ${isSelected 
                    ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-2 border-emerald-500 dark:border-emerald-500/50'
                    : showHint
                    ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-2 border-amber-200 dark:border-amber-500/30 animate-pulse'
                    : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:text-indigo-600 dark:hover:text-indigo-300'
                  }
                `}
              >
                {number}
              </button>
            );
          })}
        </div>

        {gameOver && !showGameComplete && (
          <div className="mt-8 text-center">
            <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              {language === 'en' ? 'Game Over!' : 'Partie terminée !'}
            </h3>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
              {language === 'en' 
                ? `You found ${score} prime numbers with ${mistakes} mistakes`
                : `Vous avez trouvé ${score} nombres premiers avec ${mistakes} erreurs`}
            </p>
            <button
              onClick={resetGame}
              className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
                hover:bg-indigo-700 dark:hover:bg-indigo-600 
                focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2"
            >
              {language === 'en' ? 'Play Again' : 'Rejouer'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrimeHunterGame;